import React, { useState, useEffect } from "react";
import TextBox2 from "../TextBox2";
import {
  ArrowLeft,
  Copy,
  Check,
  Pencil,
  Trash2,
  Loader2,
  X,
  MapPin,
  Clock,
  KeyRound,
  EyeOff,
  Eye,
  Link2,
  ExternalLink,
} from "lucide-react";
import { updateHostProperty } from "../../store/thunks/hostActions";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getPropertyDetails } from "../../store/thunks/hostActions";
import { deleteHostProperty } from "../../store/thunks/hostActions";
import { Alert } from "@mui/material";
import { updatePropertyReset } from "../../store/slices/hostSlices";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import RemoteLockDoorMapping from "./RemoteLockDoorMapping";

const getInitialHideAddressState = () => {
  const storedValue = localStorage.getItem("hideAddress");
  return storedValue ? JSON.parse(storedValue) : false;
};

function DisplayValue({ label, value, span }) {
  return (
    <div className={span ? "col-span-full" : ""}>
      <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm font-semibold text-gray-900 leading-snug">{value || <span className="text-gray-300 font-normal italic">Not set</span>}</p>
    </div>
  );
}

function PropertyDetailsComponent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const [checkInTime, setCheckInTime] = useState();
  const [checkOutTime, setCheckoutTime] = useState();

  const handleTimeChange = (selectedTime) => {
    setCheckInTime(selectedTime);
  };

  const handleTimeChange2 = (selectedTime) => {
    setCheckoutTime(selectedTime);
  };

  const { pid } = params;
  const [queryParam] = useSearchParams();
  const [editMode, setEditMode] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [copyText, setCopyText] = useState("Copy");

  const { property: property_d } = useSelector(
    (state) => state.propertyDetails
  );
  const { hostInfo } = useSelector((state) => state.host);

  const {
    loading: loadingUpdate,
    success: successUpdate,
    error: errorUpdate,
  } = useSelector((state) => state.propertyUpdate);

  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const [credentials, setCredentials] = useState({
    description: "",
    keyInfo: "",
    listingTitle: ""
  });

  const [hideAddress, setHideAddress] = useState(getInitialHideAddressState);

  const handleCheckboxChange = () => {
    setHideAddress(!hideAddress);
  };

  useEffect(() => {
    localStorage.setItem("hideAddress", JSON.stringify(hideAddress));
  }, [hideAddress]);

  const handleEditSubmit = () => {
    const propertyCredentials = {
      checkInTime,
      checkOutTime,
      ...credentials,
      address,
      hideAddress,
    };

    console.log(propertyCredentials);
    dispatch(updateHostProperty({ propertyCredentials, pid }));
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setIsReadOnly(true);
    if (property_d?.address) setAddress({ ...property_d.address });
    setCredentials({
      description: property_d?.description || "",
      keyInfo: property_d?.keyInfo || "",
      listingTitle: property_d?.listingTitle || "",
    });
  };

  const handleDeleteBtn = () => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      dispatch(deleteHostProperty(pid));
      navigate("/host");
    }
  };

  function unsecuredCopyToClipboard(text, description) {
    const textArea = document.createElement("textarea");
    textArea.value = description + " " + text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand("copy");
    } catch (err) {
      console.error("Unable to copy to clipboard", err);
    }
    document.body.removeChild(textArea);
  }

  useEffect(() => {
    if (queryParam.get("edit")) {
      setEditMode(true);
      setIsReadOnly(false);
    }

    if (successUpdate) {
      setIsReadOnly(true);
      setEditMode(false);
      navigate("/host");
    } else {
      if (!property_d?._id || property_d?._id !== pid) {
        dispatch(getPropertyDetails(pid));
      } else {
        setAddress({ ...property_d.address });
        setCredentials({
          description: property_d.description,
          keyInfo: property_d.keyInfo,
          listingTitle: property_d.listingTitle
        });
      }
    }

    dispatch(getPropertyDetails(pid));
  }, [dispatch, pid, property_d?._id, navigate, successUpdate]);

  useEffect(() => {
    setTimeout(() => {
      dispatch(updatePropertyReset());
    }, 2000);
  }, [errorUpdate]);

  const dateObject = new Date(property_d.checkInTime);
  const timeFormatOptions = { hour: "numeric", minute: "numeric", hour12: true };
  const formattedTime = dateObject.toLocaleTimeString(undefined, timeFormatOptions);

  const dateObject2 = new Date(property_d.checkOutTime);
  const checkoutFormat = { hour: "numeric", minute: "numeric", hour12: true };
  const formattedCheckoutTime = dateObject2.toLocaleTimeString(undefined, checkoutFormat);

  const addressLine = [address.line1, address.city, address.state, address.zipCode].filter(Boolean).join(", ");

  return (
    <div className="px-4 laptop:px-8 py-5">
      {errorUpdate && (
        <Alert severity="warning" className="mb-4">{errorUpdate}</Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 flex items-center justify-center transition-colors flex-shrink-0"
          >
            <ArrowLeft size={16} className="text-gray-600" />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-900 truncate">
              {credentials.listingTitle || address.line1 || "Property Details"}
            </h1>
            {addressLine && (
              <p className="text-xs text-gray-400 truncate mt-0.5">{addressLine}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {editMode ? (
            <>
              <button
                onClick={handleCancelEdit}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors"
              >
                <X size={14} />
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={loadingUpdate}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-primary-500 hover:bg-primary-600 rounded-xl transition-colors disabled:opacity-60 shadow-sm"
              >
                {loadingUpdate ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Save Changes
              </button>
            </>
          ) : (
            <button
              onClick={() => { setEditMode(true); setIsReadOnly(false); }}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors"
            >
              <Pencil size={14} />
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Two-column layout */}
      <div className="flex gap-5 items-start">
        {/* Main column */}
        <div className="flex-1 min-w-0 space-y-4">

          {/* Location */}
          <section className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                <MapPin size={14} className="text-gray-500" />
              </div>
              <h2 className="text-sm font-bold text-gray-900">Location</h2>
            </div>

            {isReadOnly ? (
              <div className="grid grid-cols-3 gap-x-6 gap-y-3">
                <DisplayValue label="Address" value={address.line1} />
                <DisplayValue label="Line 2" value={address.line2} />
                <DisplayValue label="City" value={address.city} />
                <DisplayValue label="State" value={address.state} />
                <DisplayValue label="Zip" value={address.zipCode} />
              </div>
            ) : (
              <>
                <div className="flex gap-3 mobile:flex-col">
                  <TextBox2 label="Address Line 1" name="line1" credentials={address} value={address.line1} setValue={setAddress} readOnly={isReadOnly} />
                  <TextBox2 label="Address Line 2" name="line2" credentials={address} value={address.line2} setValue={setAddress} readOnly={isReadOnly} />
                </div>
                <div className="flex gap-3 mt-3 mobile:flex-col">
                  <TextBox2 label="City" name="city" credentials={address} value={address.city} setValue={setAddress} readOnly={isReadOnly} />
                  <TextBox2 label="State" name="state" credentials={address} value={address.state} setValue={setAddress} readOnly={isReadOnly} />
                  <TextBox2 label="Zip Code" name="zipCode" credentials={address} value={address.zipCode} setValue={setAddress} readOnly={isReadOnly} />
                </div>
              </>
            )}
          </section>

          {/* Stay Details */}
          <section className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                <Clock size={14} className="text-gray-500" />
              </div>
              <h2 className="text-sm font-bold text-gray-900">Stay Details</h2>
            </div>

            {isReadOnly ? (
              <div className="grid grid-cols-3 gap-x-6 gap-y-3">
                <DisplayValue label="Listing Title" value={credentials.listingTitle} span />
                <DisplayValue label="Check-In" value={formattedTime} />
                <DisplayValue label="Check-Out" value={formattedCheckoutTime} />
              </div>
            ) : (
              <>
                <TextBox2 label="Listing Title" name="listingTitle" credentials={credentials} value={credentials.listingTitle} setValue={setCredentials} readOnly={isReadOnly} />
                <div className="flex gap-3 mt-3">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-700 block mb-1">Check In</label>
                    <DatePicker
                      selected={checkInTime}
                      value={checkInTime ? checkInTime : formattedTime}
                      onChange={handleTimeChange}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="h:mm aa"
                      className="w-full rounded-xl border border-gray-200/60 bg-white/80 py-3 px-4 text-gray-900 text-sm outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all"
                      readOnly={isReadOnly}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-700 block mb-1">Check Out</label>
                    <DatePicker
                      selected={checkOutTime}
                      value={checkOutTime ? checkOutTime : formattedCheckoutTime}
                      onChange={handleTimeChange2}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="h:mm aa"
                      className="w-full rounded-xl border border-gray-200/60 bg-white/80 py-3 px-4 text-gray-900 text-sm outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all"
                      readOnly={isReadOnly}
                    />
                  </div>
                </div>
              </>
            )}
          </section>

          {/* Guest Access */}
          <section className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                <KeyRound size={14} className="text-gray-500" />
              </div>
              <h2 className="text-sm font-bold text-gray-900">Guest Access</h2>
            </div>

            {isReadOnly ? (
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {credentials.description || <span className="text-gray-300 italic">No entry instructions set</span>}
              </p>
            ) : (
              <textarea
                className="w-full rounded-xl border border-gray-200/60 bg-white/80 py-3 px-4 text-sm text-gray-900 placeholder-gray-400 outline-none transition-all resize-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
                id="message"
                rows="3"
                placeholder="e.g., The lockbox is near the front door. Code is 1234."
                name="description"
                value={credentials.description}
                readOnly={isReadOnly}
                onChange={(e) =>
                  setCredentials({
                    ...credentials,
                    [e.target.name]: e.target.value,
                  })
                }
              />
            )}

            {property_d?._id === pid && hostInfo?._id && (
              <RemoteLockDoorMapping hostId={hostInfo._id} propertyId={pid} />
            )}
          </section>
        </div>

        {/* Sidebar */}
        <div className="hidden laptop:flex flex-col w-[260px] flex-shrink-0 space-y-4">

          {/* Guest Link */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Link2 size={14} className="text-gray-400" />
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Guest Link</h3>
            </div>
            <p className="text-xs text-gray-400 mb-3 leading-relaxed">
              Share this link with guests so they can order groceries to the property.
            </p>
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  const description =
                    "I also offer a grocery delivery and stocking service through Bellhopt for my guests to make grocery shopping less stressful and time-consuming, especially in a new place. They deliver fresh, high-quality products to your rental before you even arrive. If you'd like to order you can order directly to the rental Order Link: ";
                  unsecuredCopyToClipboard(property_d.uniqueLink, description);
                  setCopyText("Copied!");
                  setTimeout(() => { setCopyText("Copy"); }, 3000);
                  e.stopPropagation();
                }}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl transition-colors ${
                  copyText === "Copied!"
                    ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {copyText === "Copied!" ? <Check size={12} /> : <Copy size={12} />}
                {copyText === "Copied!" ? "Copied!" : "Copy Message"}
              </button>
              {property_d.uniqueLink && (
                <a
                  href={property_d.uniqueLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
                >
                  <ExternalLink size={13} />
                </a>
              )}
            </div>
          </div>

          {/* Visibility */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              {hideAddress ? <EyeOff size={14} className="text-amber-500" /> : <Eye size={14} className="text-gray-400" />}
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Hide Address</h3>
            </div>
            <button
              onClick={isReadOnly ? undefined : handleCheckboxChange}
              disabled={isReadOnly}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-left transition-all ${
                hideAddress
                  ? "bg-amber-50 border-amber-200"
                  : "bg-gray-50 border-gray-100"
              } ${isReadOnly ? "cursor-default opacity-70" : "cursor-pointer hover:border-gray-300"}`}
            >
              <span className={`text-xs font-medium ${hideAddress ? "text-amber-800" : "text-gray-600"}`}>
                {hideAddress ? "Address hidden from guests" : "Address visible to guests when shopping on Bellhopt"}
              </span>
              <div className={`w-8 h-[18px] rounded-full flex items-center transition-colors ${hideAddress ? "bg-amber-400" : "bg-gray-300"}`}>
                <span className={`block w-3.5 h-3.5 rounded-full bg-white shadow-sm transition-transform ${hideAddress ? "translate-x-[15px]" : "translate-x-[3px]"}`} />
              </div>
            </button>
          </div>

          {/* Danger Zone */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <button
              onClick={handleDeleteBtn}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-500 bg-red-50 hover:bg-red-100 border border-red-100 rounded-xl transition-colors"
            >
              <Trash2 size={12} />
              Delete Property
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PropertyDetailsComponent;
